// libde265 WASM (@yume-chan/libde265 1.0.0, LGPL-3.0). See public/hevc/libde265-LICENSE.txt.

function assetUrl(file) {
  return `${process.env.PUBLIC_URL || ""}/hevc/${file}`;
}

let modulePromise = null;

function loadModule() {
  if (!modulePromise) {
    const wasmUrl = assetUrl("libde265.wasm");
    modulePromise = (async () => {
      const factoryModule = await import(/* webpackIgnore: true */ assetUrl("libde265.mjs"));
      const response = await fetch(wasmUrl);
      if (!response.ok) throw new Error("HEVC decoder failed to load");
      const factory = factoryModule.default;
      return factory({ wasmBinary: await response.arrayBuffer() });
    })().catch((error) => {
      modulePromise = null;
      throw error;
    });
  }
  return modulePromise;
}

export function copyImagePlane(plane, bitsPerPixel) {
  const width = plane.width;
  const height = plane.height;
  const bytes = plane.bytes;
  const stride = plane.stride || width;
  const out = new Uint16Array(width * height);
  const bytesPerSample = bitsPerPixel > 8 ? 2 : 1;
  const packedLength = width * height * bytesPerSample;
  const rowStride = bytes.length === packedLength ? width * bytesPerSample : stride;

  if (bytesPerSample === 1) {
    for (let row = 0; row < height; row += 1) {
      const start = row * rowStride;
      for (let col = 0; col < width; col += 1) {
        out[row * width + col] = bytes[start + col];
      }
    }
    return out;
  }

  const samples = new Uint16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / 2));
  const sampleStride = rowStride / 2;
  for (let row = 0; row < height; row += 1) {
    const start = row * sampleStride;
    for (let col = 0; col < width; col += 1) {
      out[row * width + col] = samples[start + col];
    }
  }
  return out;
}

function readImage(image) {
  const bitsPerPixel = image.getBitsPerPixel(0);
  const y = image.getImagePlane(0);
  const cb = image.getImagePlane(1);
  const cr = image.getImagePlane(2);
  const fullRange = typeof image.isFullRange === "function" ? image.isFullRange() : image.isFullRange;
  return {
    y: copyImagePlane(y, bitsPerPixel),
    cb: copyImagePlane(cb, bitsPerPixel),
    cr: copyImagePlane(cr, bitsPerPixel),
    width: y.width,
    height: y.height,
    chromaWidth: cb.width,
    chromaHeight: cb.height,
    bitDepth: bitsPerPixel > 8 ? bitsPerPixel : 8,
    fullRange: Boolean(fullRange),
  };
}

function isWaiting(module, error) {
  return error === module.Error.ERROR_WAITING_FOR_INPUT_DATA;
}

export async function createHevcDecoder() {
  const module = await loadModule();
  const decoder = new module.Decoder();

  const takePictures = () => {
    const frames = [];
    let more = true;
    while (more) {
      const result = decoder.decode();
      more = result.more;
      if (!module.isOk(result.error)) {
        if (isWaiting(module, result.error)) break;
        throw new Error(module.getErrorText(result.error));
      }
      const image = decoder.getNextPicture();
      if (!image) continue;
      try {
        frames.push(readImage(image));
      } finally {
        image.delete();
      }
    }
    return frames;
  };

  return {
    feed(data) {
      if (!data?.length) return [];
      const pushed = decoder.pushData(data, 0n);
      if (!module.isOk(pushed) && !isWaiting(module, pushed)) {
        throw new Error(module.getErrorText(pushed));
      }
      decoder.pushEndOfFrame();
      return takePictures();
    },
    destroy() {
      decoder.delete();
    },
  };
}
